import { useQuery } from '@tanstack/react-query';
import { useStorage } from '@/context/StorageContext';
import { useAuth } from '@/context/AuthContext';
import { HttpStorageAdapter } from '@/services/storage/httpStorageAdapter';
import type { Conversation } from '@/types/chat.types';
import type { Folder, TrashedConversation } from '@/types/storage.types';

/**
 * Single source of truth for the signed-in user's conversations.
 *
 * App.tsx and ConversationHistory used to keep parallel `conversations`
 * state, each loading from `adapter.loadConversations()` independently and
 * coordinating refresh via a `refreshTrigger` prop. With React Query both
 * subscribe to the same query, so they always see the same data and any
 * mutation that calls `queryClient.invalidateQueries(CONVERSATIONS_QUERY_KEY)`
 * refreshes both views automatically.
 *
 * The query key includes `user.id` so cross-account switches don't surface
 * the previous user's cache.
 */
export const CONVERSATIONS_QUERY_KEY = ['conversations'] as const;
export const FOLDERS_QUERY_KEY = ['folders'] as const;
export const TRASH_QUERY_KEY = ['trash'] as const;

export function useConversations() {
  const { adapter, isInitialized } = useStorage();
  const { user } = useAuth();
  const userId = user?.id ?? null;

  return useQuery({
    queryKey: [...CONVERSATIONS_QUERY_KEY, userId] as const,
    queryFn: async (): Promise<Conversation[]> => {
      const all = await adapter.loadConversations();
      if (adapter instanceof HttpStorageAdapter) {
        // Backend already scopes /api/conversations by user_id; the
        // client-side filter is only needed for adapters that list across
        // users (e.g. SQLiteAdapter in test mode with 'local-user' fallback).
        return all;
      }
      const currentUserId = userId ?? 'local-user';
      return all.filter((conv) => conv.userId === currentUserId);
    },
    enabled: isInitialized && !!adapter && !!userId,
    placeholderData: [],
  });
}

export function useFolders() {
  const { adapter, isInitialized } = useStorage();
  const { user } = useAuth();
  const userId = user?.id ?? null;

  return useQuery({
    queryKey: [...FOLDERS_QUERY_KEY, userId] as const,
    queryFn: async (): Promise<Folder[]> => adapter.loadFolders(),
    enabled: isInitialized && !!adapter && !!userId,
    placeholderData: [],
  });
}

/**
 * Load all soft-deleted conversations in the trash bin.
 *
 * Returns a React Query result keyed by user.id, so cross-account switches
 * don't leak cache. Used by the TrashBinSection sidebar component.
 */
export function useTrash() {
  const { adapter, isInitialized } = useStorage();
  const { user } = useAuth();
  const userId = user?.id ?? null;

  return useQuery({
    queryKey: [...TRASH_QUERY_KEY, userId] as const,
    queryFn: async (): Promise<TrashedConversation[]> =>
      adapter.loadTrash(),
    enabled: isInitialized && !!adapter && !!userId,
    placeholderData: [],
  });
}
